import { useState, useMemo, useCallback, useEffect } from 'react';
import { IconPlus } from '@tabler/icons-react';
import * as Sentry from '@sentry/react';

import { getNetworkError } from '../core/utils';
import { getDesignations as getDesignationsApi, createDesignation, updateDesignation, deleteDesignation } from '../api';
import { useDonations } from '../store/donations/hook';
import AlertMessage from '../components/alerts/AlertMessage';
import Loader from '../components/loader/Loader';
import Button from '../components/buttons/Button';
import DesignationsTable from '../components/designations/DesignationsTable';
import DesignationModal from '../components/designations/DesignationModal';

const Designations = () => {
  const [loadingDesignations, setLoadingDesignations] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState('');
  const [designations, setDesignations] = useState([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const { donations, getDonations, loading: loadingDonations } = useDonations();
  const loading = loadingDesignations || loadingDonations;

  const donationTotals = useMemo(() => {
    const totals = {};
    donations.forEach((donation) => {
      if (donation.designationId) {
        totals[donation.designationId] = (totals[donation.designationId] || 0) + (donation.netAmount || 0);
      }
    });
    return totals;
  }, [donations]);

  const designationsWithTotals = useMemo(
    () => designations.map((d) => ({ ...d, totalDonations: donationTotals[d.designationId] || 0 })),
    [designations, donationTotals],
  );

  const fetchDesignations = useCallback(async () => {
    setError(false);
    setLoadingDesignations(true);
    try {
      const data = await getDesignationsApi();
      setDesignations(data);
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
    }
    setLoadingDesignations(false);
  }, []);

  useEffect(() => {
    fetchDesignations();
    getDonations();
  }, [fetchDesignations, getDonations]);

  const handleCreate = useCallback(
    async ({ name, code }) => {
      setError(false);
      setLoadingDesignations(true);
      try {
        await createDesignation({ name, code });
        setSuccess('Designation created successfully!');
        await fetchDesignations();
      } catch (err) {
        Sentry.captureException(err);
        setError(getNetworkError(err));
      }
      setLoadingDesignations(false);
    },
    [fetchDesignations],
  );

  const handleEdit = useCallback(
    async (designationId, { name, code }) => {
      setError(false);
      setLoadingDesignations(true);
      try {
        await updateDesignation(designationId, { name, code });
        setSuccess('Designation updated successfully!');
        await fetchDesignations();
      } catch (err) {
        Sentry.captureException(err);
        setError(getNetworkError(err));
      }
      setLoadingDesignations(false);
    },
    [fetchDesignations],
  );

  const handleDelete = useCallback(
    async (designationId) => {
      setError(false);
      setLoadingDesignations(true);
      try {
        await deleteDesignation(designationId);
        setSuccess('Designation deleted successfully!');
        await fetchDesignations();
      } catch (err) {
        Sentry.captureException(err);
        setError(getNetworkError(err));
      }
      setLoadingDesignations(false);
    },
    [fetchDesignations],
  );

  return (
    <div className="w-full space-y-6">
      <div className="flex justify-between space-x-3">
        <h1 className="mb-auto text-h3 dark:text-white-100">Donation Designations</h1>

        <div className="flex items-center space-x-3">
          <Button
            title="New Designation"
            color="primary"
            LeftIcon={IconPlus}
            onClick={() => setCreateModalOpen(true)}
          />
        </div>
      </div>

      <div className="relative">
        <DesignationsTable
          data={designationsWithTotals}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
        />

        {loading && <Loader />}
      </div>

      <DesignationModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} onSave={handleCreate} />

      <AlertMessage
        open={!!success}
        message={typeof success === 'string' ? success : 'Operation completed successfully!'}
        onClose={() => setSuccess('')}
        severity="success"
      />

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        onClose={() => setError(false)}
        severity="error"
      />
    </div>
  );
};

export default Designations;
